import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface ReferralStats {
  code: string;
  referralLink: string;
  totalReferrals: number;
  pendingReferrals: number;
  completedReferrals: number;
  totalCreditsEarned: number;
}

export interface ReferralItem {
  id: string;
  referredUserName: string | null;
  status: 'pending' | 'completed';
  createdAt: string;
  firstPurchaseAt: string | null;
  creditsAwarded: number;
}

interface ReferralCodeRow {
  code: string;
}

interface ReferralRow {
  id: string;
  status: string;
  created_at: string;
  first_purchase_at: string | null;
  credits_awarded: number;
  referred: { full_name: string | null } | null;
}

export function useReferral() {
  const { user } = useAuth();
  const [stats, setStats] = useState<ReferralStats>({
    code: '',
    referralLink: '',
    totalReferrals: 0,
    pendingReferrals: 0,
    completedReferrals: 0,
    totalCreditsEarned: 0
  });
  const [referrals, setReferrals] = useState<ReferralItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    if (user) {
      fetchReferralData();
    }
  }, [user]);
  
  const fetchReferralData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user profile ID
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user?.id)
        .single();

      if (profileError) throw profileError;
      if (!profile) throw new Error('Profile not found');

      // Get referral code - using raw query to bypass type checking for new tables
      const { data: codeData, error: codeError } = await supabase
        .from('referral_codes' as any)
        .select('code')
        .eq('user_id', profile.id)
        .single() as { data: ReferralCodeRow | null; error: any };

      if (codeError) throw codeError;

      const code = codeData?.code || '';
      const baseUrl = window.location.origin;
      const referralLink = `${baseUrl}/cadastro?ref=${code}`;

      // Get referrals list
      const { data: referralsData, error: referralsError } = await supabase
        .from('referrals' as any)
        .select(`
          id,
          status,
          created_at,
          first_purchase_at,
          credits_awarded,
          referred:referred_id (
            full_name
          )
        `)
        .eq('referrer_id', profile.id)
        .order('created_at', { ascending: false }) as { data: ReferralRow[] | null; error: any };

      if (referralsError) throw referralsError;

      // Calculate stats
      const totalReferrals = referralsData?.length || 0;
      const pendingReferrals = referralsData?.filter(r => r.status === 'pending').length || 0;
      const completedReferrals = referralsData?.filter(r => r.status === 'completed').length || 0;
      const totalCreditsEarned = referralsData?.reduce((sum, r) => sum + (r.credits_awarded || 0), 0) || 0;

      // Format referrals list
      const formattedReferrals: ReferralItem[] = referralsData?.map(r => ({
        id: r.id,
        referredUserName: r.referred?.full_name || null,
        status: r.status as 'pending' | 'completed',
        createdAt: r.created_at,
        firstPurchaseAt: r.first_purchase_at,
        creditsAwarded: r.credits_awarded || 0
      })) || [];

      setStats({
        code,
        referralLink,
        totalReferrals,
        pendingReferrals,
        completedReferrals,
        totalCreditsEarned
      });

      setReferrals(formattedReferrals);
    } catch (err) {
      console.error('Error fetching referral data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(stats.referralLink);
  };

  return { 
    stats, 
    referrals, 
    loading, 
    error,
    refetch: fetchReferralData,
    copyLink
  };
}
